<template>
  <div
    class="modal fade"
    id="userDetailsModal"
    tabindex="-1"
    aria-labelledby="userDetailsModalLabel"
    aria-hidden="true"
  >
    <div
      class="modal-dialog modal-rl modal-dialog-centered modal-dialog-scrollable"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title text-title" id="userDetailsModalLabel">
            {{ $t("user_page.details_title") }}
          </h5>
          <button
            type="button"
            class="btn-close p-0"
            data-bs-dismiss="modal"
            :aria-label="$t('common.close')"
          >
            <img
              src="../../../assets/img/icons/close-circle-2.svg"
              :alt="$t('common.close')"
            />
          </button>
        </div>
        <div class="modal-body p-0">
          <ul v-if="user" class="details-list list-style">
            <li>
              <span class="fs-14 text-title">{{ $t("user_page.full_name") }}</span>
              <span class="fs-14 fw-bold text-title">{{ fullName || $t("user_page.not_available") }}</span>
            </li>
            <li>
              <span class="fs-14 text-title">{{ $t("user_page.username") }}</span>
              <span class="fs-14 fw-bold text-title">{{ user.username || $t("user_page.not_available") }}</span>
            </li>
            <li>
              <span class="fs-14 text-title">{{ $t("user_page.email") }}</span>
              <span class="fs-14 fw-bold text-title">{{ user.email || $t("user_page.not_available") }}</span>
            </li>
            <li>
              <span class="fs-14 text-title">{{ $t("user_page.phone") }}</span>
              <span class="fs-14 fw-bold text-title">{{ user.phone || $t("user_page.not_available") }}</span>
            </li>
            <li>
              <span class="fs-14 text-title">{{ $t("user_page.warehouse") }}</span>
              <span class="fs-14 fw-bold text-title">{{ user.warehouse_name || user.warehouse_code || $t("user_page.not_available") }}</span>
            </li>
            <li>
              <span class="fs-14 text-title">{{ $t("user_page.branch") }}</span>
              <span class="fs-14 fw-bold text-title">{{ user.branch_name || user.branch_code || $t("user_page.not_available") }}</span>
            </li>
            <li>
              <span class="fs-14 text-title">{{ $t("user_page.biller") }}</span>
              <span class="fs-14 fw-bold text-title">{{ user.biller_name || user.biller_code || $t("user_page.not_available") }}</span>
            </li>
            <li>
              <span class="fs-14 text-title">{{ $t("user_page.role_permission") }}</span>
              <span class="fs-14 fw-bold text-title">{{ formatRole(user.role_permission_id) }}</span>
            </li>
            <li>
              <span class="fs-14 text-title">{{ $t("user_page.gender") }}</span>
              <span class="fs-14 fw-bold text-title">{{ formatGender(user.gender) }}</span>
            </li>
            <li>
              <span class="fs-14 text-title">{{ $t("user_page.company") }}</span>
              <span class="fs-14 fw-bold text-title">{{ user.company || $t("user_page.not_available") }}</span>
            </li>
            <li>
              <span class="fs-14 text-title">{{ $t("user_page.address") }}</span>
              <span class="fs-14 fw-bold text-title">{{ user.address || $t("user_page.not_available") }}</span>
            </li>
            <li>
              <span class="fs-14 text-title">{{ $t("user_page.status") }}</span>
              <span :class="['badge fs-14 fw-medium', (user.active || user.status === 'active') ? 'badge-success' : 'badge-danger']">
                {{ (user.active || user.status === 'active') ? $t("user_page.status_active") : $t("user_page.status_inactive") }}
              </span>
            </li>
            <li>
              <span class="fs-14 text-title">{{ $t("user_page.created_at") }}</span>
              <span class="fs-14 fw-bold text-title">{{ formatDateTime(user.created_at) }}</span>
            </li>
            <li v-if="user.last_login">
              <span class="fs-14 text-title">{{ $t("user_page.last_login") }}</span>
              <span class="fs-14 fw-bold text-title">{{ formatDateTime(user.last_login) }}</span>
            </li>
            <li v-if="user.ip_address">
              <span class="fs-14 text-title">{{ $t("user_page.ip_address") }}</span>
              <span class="fs-14 fw-bold text-title">{{ user.ip_address }}</span>
            </li>
          </ul>
          <div v-else class="p-30 text-center">
            <span class="text-paragraph">{{ $t("user_page.no_user_selected") }}</span>
          </div>
        </div>
        <div class="modal-footer pt-20 pb-35 justify-content-end">
          <button
            type="button"
            class="btn style-four px-xxl-6"
            data-bs-dismiss="modal"
          >
            {{ $t("common.close") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import type { User } from "../../../services/user";
import type { RolePermission } from "../../../services/role_permission";

export default defineComponent({
  name: "UserDetails",
  props: {
    user: {
      type: Object as PropType<User | null>,
      default: null,
    },
    roles: {
      type: Array as PropType<RolePermission[]>,
      default: () => [],
    },
  },
  setup(props) {
    const { t, locale } = useI18n();

    const fullName = computed(() =>
      `${props.user?.first_name || ""} ${props.user?.last_name || ""}`.trim()
    );

    const formatRole = (roleId?: number) => {
      if (!roleId) return t("user_page.not_available");
      const matchedRole = props.roles.find((role) => Number(role.id) === Number(roleId));
      const roleName = matchedRole?.role_name || "";
      const roleKeyMap: Record<string, string> = {
        "super admin": "user_page.role_super_admin",
        admin: "user_page.role_admin",
        manager: "user_page.role_manager",
        cashier: "user_page.role_cashier",
      };
      const normalizedRole = roleName.trim().toLowerCase();
      if (roleKeyMap[normalizedRole]) {
        return t(roleKeyMap[normalizedRole]);
      }
      return roleName || t("user_page.role_with_id", { id: roleId });
    };

    const formatGender = (gender?: number) => {
      if (gender === 1) return t("user_page.gender_male");
      if (gender === 0) return t("user_page.gender_female");
      return t("user_page.not_available");
    };

    const formatDateTime = (value?: string) => {
      if (!value) {
        return t("user_page.not_available");
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return String(value);
      }

      return date.toLocaleString(locale.value === "km" ? "km-KH" : "en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "numeric",
        minute: "2-digit",
      });
    };

    return {
      fullName,
      formatRole,
      formatGender,
      formatDateTime,
    };
  },
});
</script>
